import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Roles } from '@app/core/enums/roles.enum';
import { ACCESS } from '@app/core/constant/access';
import { ViewPoint } from '@app/core/enums/view-point.enum';
import { AuthService } from '@services/auth/auth.service';
import { StorageService } from '@services/storage/storage.service';
import { UserAccessService } from '@services/user-access/user-access.service';
import { WindowRefService } from '@services/window-ref/window-ref.service';
import { get, isEmpty } from 'lodash-es';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { State } from '@app/shared/store/reducers';
import { select, Store } from '@ngrx/store';
import { getCurrUser } from '@app/shared/store/selectors/user-claims.selector';
import { NavigationTile } from '@app/core/interfaces/navigation-tile.interface';
import { FeatureType } from '@app/core/enums/feature-type.enum';
import { ImageColorType } from '@app/core/enums/image-color-type.enum';
import { FeatureImages } from '@app/core/utils/feature-images';
import * as _ from 'lodash';
import { CreateCastMemberModalComponent } from '@app/shared/modals/admin/create-cast-member-modal/create-cast-member-modal.component';
import { CreateCharacterModalComponent } from '@app/shared/modals/admin/create-character-modal/create-character-modal.component';
import { keys, LocalStorageService } from '@app/core/services/storage/local-storage.service';
import { DataService } from '@app/core/services/api';

@Component({
  standalone: false,
  selector: 'app-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
})

export class PageHeaderComponent implements OnInit, OnDestroy {
  @ViewChild(CreateCastMemberModalComponent) createCastModal: CreateCastMemberModalComponent;
  @ViewChild(CreateCharacterModalComponent) createCharModal: CreateCharacterModalComponent;
  @ViewChild('sidePocketPanel', { static: true }) sidePocketPanel: ElementRef;
  @ViewChild('profilePocketPanel', { static: true }) profilePocketPanel: ElementRef;

  public nameInitials: string;

  public appEnvironment: string;
  public appName = '';
  public appTitle = '';
  public appVersion: string;
  public backButton = '';
  public initials: string;
  public hamburgerMenu = true;
  public hamburgerMobileMenu = true;
  public showHamburgerIcon = true;
  public homePage: false;
  public showBackButton: false;
  public userName = '';
  public userBadge = '';

  public appShortName: string;
  public auth: any;
  public environment: string;
  public languageType: any[] = [];
  public languageTypeMock: any[] = [];
  public languageValue: any;
  public user: any;
  public roles = [];
  public access = ACCESS;
  public tiles: NavigationTile[] = [];
  public translations: any;

  public languageTypeSelected: { id: number; name: string; selected?: boolean; };

  public subscriptions: Subscription[] = [];

  public userState$: Subscription;
  public isUpdate: boolean;
  public tileTypes = [
    'Admin',
    'CastMeasurements',
    'CreateCharacter'
  ];

  public unitOfMeasure: any[] = [
    { text: 'inches', value: 'in' },
    { text: 'cm', value: 'cm' }
  ];
  public preferredUnit: any;

  constructor(
    private store: Store<State>,
    private authService: AuthService,
    private route: ActivatedRoute,
    public elementRef: ElementRef,
    private router: Router,
    private userAccessService: UserAccessService,
    private windowRef: WindowRefService,
    private _localStorage: LocalStorageService,
    private storageService: StorageService,
    private dataService: DataService,
  ) { }

  openLeftPanel() {
    this.sidePocketPanel.nativeElement.open();
  }

  openRightPanel() {
    this.user = this.userAccessService.getUser();
    this.roles = _.get(this.user, 'roles', []);
    this.initials = _.get(this.user, 'nameAbbv', '');
    this.languageValue = _.get(this.user, 'userProfile.preferredLanguageTypeId', '1');
    this.profilePocketPanel.nativeElement.open();
  }

  public async ngOnInit() {
    this.userState$ = this.store.pipe(select(getCurrUser)).subscribe((res: any) => {
      if (res) {
        this.updateNavigationTiles(this.tileTypes);
      }
    });
    this.showHamburgerIcon = true;
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.route),
      map(route => {
        while (route.firstChild) {
          route = route.firstChild;
        }

        return route.snapshot;
      }))
      .subscribe(snapshot => {
        /* istanbul ignore next */
        if (isEmpty(snapshot.data)) {
          snapshot.data = snapshot.parent.data;
        }

        this.appShortName = environment.appShortName;
        this.appName = environment.appName;
        this.backButton = get(snapshot, 'data.backButton', this.backButton);
        this.homePage = get(snapshot, 'data.homePage', this.homePage);
        this.hamburgerMenu = get(snapshot, 'data.hamburgerMenu', this.hamburgerMenu);
        this.hamburgerMobileMenu = get(snapshot, 'data.hamburgerMobileMenu', this.hamburgerMobileMenu);
        this.showBackButton = get(snapshot, 'data.showBackButton', this.showBackButton);
      });

    this.appVersion = environment.appVersion;
    this.appName = environment.appName;
    this.appShortName = environment.appShortName;
    this.user = this.userAccessService.getUser();
    this.roles = _.get(this.user, 'roles', []);
    this.initials = _.get(this.user, 'nameAbbv', '');
    this.languageValue = _.get(this.user, 'userProfile.preferredLanguageTypeId', '0');
    if (this.user) {
      await this.getPreferredUnit(this.user);
    }
  }

  public updateNavigationTiles(tileTypes: any[]) {
    if (!tileTypes) {
      return;
    }

    this.tiles = tileTypes.map((type) => this.navigationTileFromType(type));
    this.tiles = this.tiles.filter(item => {
      const hasAnyPermission = this.authService.hasAnyAbility(item.requiredAbilities);

      return hasAnyPermission || _.isEmpty(item.requiredAbilities);
    });

  }

  private navigationTileFromType(type: FeatureType): NavigationTile {
    switch (type) {
      case FeatureType.Admin:
        return {
          type: FeatureType.Admin,
          title: 'Admin',
          color: FeatureImages.getImageBackgroundColor({ featureType: FeatureType.Admin }),
          image: FeatureImages.getImagePath({ featureType: FeatureType.Admin, colorType: ImageColorType.Inverted }),
          route: '/admin',
          requiredAbilities: ACCESS.CTA.ADMIN,
          url: ''
        };
      case FeatureType.CastMeasurements:
        return {
          type: FeatureType.CreateCast,
          title: 'Create Cast',
          color: FeatureImages.getImageBackgroundColor({ featureType: FeatureType.CreateCast }),
          image: FeatureImages.getImagePath({ featureType: FeatureType.CreateCast, colorType: ImageColorType.Inverted }),
          route: '',
          requiredAbilities: ACCESS.CTA.CAST_EDITOR,
          url: ''
        };
      case FeatureType.CreateCharacter:
        return {
          type: FeatureType.CreateCharacter,
          title: 'Create Character',
          color: FeatureImages.getImageBackgroundColor({ featureType: FeatureType.CreateCharacter }),
          image: FeatureImages.getImagePath({ featureType: FeatureType.CreateCharacter, colorType: ImageColorType.Inverted }),
          route: '',
          requiredAbilities: ACCESS.CTA.CHARACTER_EDITOR,
          url: ''
        };
    }
  }

  public goToLink(tile: NavigationTile) {
    this.sidePocketPanel.nativeElement.close();
    if (tile.route != '') {
      this.router.navigate([tile.route]);
    }

    if (tile.type === FeatureType.CreateCast) {
      this.createCastModal.open();
    }

    if (tile.type === FeatureType.CreateCharacter) {
      this.isUpdate = false;
      this.createCharModal.open(false);
    }
  }

  public closeSidePocketPanel() {
    this.sidePocketPanel.nativeElement.close();
  }

  public logout() {
    this.authService.logout(true);
  }

  public goToHome() {
    window.location.href = '/';
  }

  /**
   * @param  {string} route
   */
  public handleBackButton(route: string) {
    /* istanbul ignore else */
    if (route === '/home') {
      this.windowRef.nativeWindow.open('/', '_self');
    } else {
      this.router.navigate([route, this.route.snapshot.params]);
    }
  }

  public isMobileView() {
    const innerWidth = window.innerWidth;
    const mobileWindow = ViewPoint.xsMax;

    return innerWidth > mobileWindow ? false : true;
  }

  public isAdmin() {
    return this.authService.hasRole(Roles.Admin);
  }

  public async setUnitOfMeasure(event) {
    localStorage.setItem(keys.unit, event.target.value);
    const userProfile = {
      userId: this.user.hubId,
      userProfileAttributeTypeCode: 'MEASTYPE',
      userProfileAttributeValue: localStorage.getItem(keys.unit)
  };
    await firstValueFrom(this.dataService.createUnitMeasure(userProfile));
    if (window.location.href.indexOf('cast-measurement') > -1 || window.location.href.indexOf('character-measurement') > -1) {
      window.location.reload();
    }
  }

  public async getPreferredUnit(user: any) {
    await firstValueFrom(this.dataService.checkUser(user.hubId)).then(async res => {
      if (res.userProfileAttributes.some(e => e.userProfileAttributeTypeCode === 'MEASTYPE')) {
        res.userProfileAttributes.forEach((element) => {
          if (element.userProfileAttributeTypeCode === 'MEASTYPE') {
            this.preferredUnit = element.userProfileAttributeValue;
            localStorage.setItem(keys.unit, this.preferredUnit);
          }
        });
      } else {
        localStorage.setItem(keys.unit, 'inches');
        this.preferredUnit = localStorage.getItem(keys.unit);
        const userProfile = {
          userId: this.user.hubId,
          userProfileAttributeTypeCode: 'MEASTYPE',
          userProfileAttributeValue: this.preferredUnit
      };
        await firstValueFrom(this.dataService.createUnitMeasure(userProfile));
      }
    });
  }

  ngOnDestroy(): void {
    this.userState$.unsubscribe();
  }
}
